import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';
import { Theme } from '@astryxdesign/core/theme';
import { ToastViewport } from '@astryxdesign/core/Toast';
import { Button } from '@astryxdesign/core/Button';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Spinner } from '@astryxdesign/core/Spinner';
import { Center } from '@astryxdesign/core/Center';
import { WarningCircle } from '@phosphor-icons/react';
import { StudioContext, useStudioQuery } from './studio';
import { studioTheme } from './theme';
import { BottomNav } from '../components/BottomNav';
import { BookingFlow } from '../pages/booking/BookingFlow';
import { ApiError, humanError } from '../lib/api';
import { registerStudioWorker } from '../lib/pwa';

const FALLBACK_ACCENT = '#4690FF';

export function StudioLayout() {
  const { slug = '' } = useParams();
  const q = useStudioQuery(slug);
  const { pathname } = useLocation();
  const isOwner = pathname.includes('/owner');
  const theme = useMemo(() => studioTheme(q.data?.accent ?? FALLBACK_ACCENT), [q.data?.accent]);

  useEffect(() => {
    void registerStudioWorker(slug);
  }, [slug]);

  useEffect(() => {
    if (q.data && !isOwner) document.title = q.data.name;
  }, [q.data, isOwner]);

  // scroll to top on page change (not on booking-step changes)
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);

  let body;
  if (q.isPending) {
    body = (
      <Center height="100dvh">
        <Spinner size="xl" aria-label="Загружаем студию" />
      </Center>
    );
  } else if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.code === 'studio_not_found';
    body = (
      <Center height="100dvh" padding={6}>
        <EmptyState
          icon={<WarningCircle size={40} aria-hidden />}
          title={notFound ? 'Студия не найдена' : 'Не удалось загрузить'}
          description={notFound ? 'Проверьте ссылку или спросите её у студии.' : humanError(q.error)}
          actions={!notFound && <Button label="Повторить" variant="primary" clickAction={() => q.refetch().then(() => undefined)} />}
        />
      </Center>
    );
  } else {
    body = (
      <StudioContext.Provider value={q.data}>
        <Outlet />
        {!isOwner && <BottomNav slug={slug} />}
        {!isOwner && <BookingFlow />}
      </StudioContext.Provider>
    );
  }

  return (
    <Theme theme={theme} mode="dark">
      {body}
      <ToastViewport />
    </Theme>
  );
}
